import { Button, ClipboardButton, TextInput, Tooltip, useCopyToClipboard } from '@neo4j-ndl/react';
import { Square2StackIconOutline } from '@neo4j-ndl/react/icons';
import { getFlag } from '@nx/launch-darkly-service';
import React from 'react';

// TODO: Replace with a needle component when it becomes available.
// The needle component should have a version with the copy text.
const CopyButtonWithIconAndText = ({
  textToCopy,
  tooltipProps = {
    isPortaled: true,
    type: 'simple',
  },
  htmlAttributes,
}: {
  textToCopy: string;
  tooltipProps?: Omit<React.ComponentProps<typeof Tooltip>, 'children'>;
  htmlAttributes?: React.ComponentPropsWithoutRef<'button'>;
}) => {
  const [, copyToClipboard] = useCopyToClipboard();
  const [timeoutId, setTimeoutId] = React.useState<number | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);

  const handleClick = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    const newTimeoutId = window.setTimeout(() => {
      setTimeoutId(null);
    }, 2000);
    setTimeoutId(newTimeoutId);
  };

  const handleCopyToClipboard = () => {
    Promise.resolve(copyToClipboard(textToCopy)).catch((error) => {
      // eslint-disable-next-line no-console
      console.error('Failed to copy text:', error);
    });
  };

  const handleCloseTooltip = () => {
    setIsOpen(false);
  };

  const handleOpenTooltip = () => {
    setIsOpen(true);
  };

  const content = timeoutId === null ? 'Copy to clipboard' : 'Copied to clipboard';

  return (
    /* Controlling the tip to make it disappear in a timely manner*/
    <Tooltip isOpen={isOpen || timeoutId !== null} {...tooltipProps}>
      <Tooltip.Trigger
        htmlAttributes={{
          onMouseEnter: handleOpenTooltip,
          onMouseLeave: handleCloseTooltip,
          onFocus: handleOpenTooltip,
          onBlur: handleCloseTooltip,
        }}
        hasButtonWrapper
      >
        <Button
          size="small"
          color="neutral"
          fill="outlined"
          onClick={(event) => {
            handleCopyToClipboard();
            handleClick();
          }}
          htmlAttributes={htmlAttributes}
        >
          <Square2StackIconOutline className="mr-2 h-4 w-4" />
          Copy
        </Button>
      </Tooltip.Trigger>
      <Tooltip.Content>{content}</Tooltip.Content>
    </Tooltip>
  );
};

export function CopyTextInput({ label, value, isPortaled }: { label: string; value: string; isPortaled?: boolean }) {
  const isUpxConsistencyEnabled = getFlag('upxConsistency', false);

  return (
    <TextInput
      label={label}
      isFluid
      size="medium"
      value={value}
      htmlAttributes={{
        'aria-label': label,
        readOnly: true,
      }}
      rightElement={
        isUpxConsistencyEnabled ? (
          <span className="mr-[-2px] inline-flex items-center">
            <CopyButtonWithIconAndText
              textToCopy={value}
              htmlAttributes={{ 'aria-label': 'Copy to clipboard' }}
              tooltipProps={{
                isPortaled,
                type: 'simple',
              }}
            />
          </span>
        ) : (
          <span className="relative">
            <ClipboardButton
              textToCopy={value}
              htmlAttributes={{ 'aria-label': 'Copy to clipboard' }}
              tooltipProps={{
                isPortaled,
                type: 'simple',
              }}
            />
          </span>
        )
      }
    />
  );
}
